import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSpecification } from '../../../models/specificationModel';
import { createRequisition, getBudgetItems } from '../../../models/requisitionModel';
import { toast } from 'react-toastify';
import { useAuth } from '../../../context/AuthContext';
import Input from '../../../components/common/Input';
import Select from '../../../components/common/Select';
import WysiwygEditor from '../../../components/common/WysiwygEditor';
import MultiFileInput from '../../../components/common/MultiFileInput';
import { safeParseDate, formatDateForAPI, formatDateForDisplay } from '../../../utils/dateUtils';

function CreateRequisition() {
  const navigate = useNavigate();
  const { specificationId } = useParams();
  const { user, hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [specification, setSpecification] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [budgetItems, setBudgetItems] = useState([]);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Check if user is IT or Super Admin - using same logic as Sidebar
  // TEMPORARILY DISABLED: Budget items not ready for production
  const isIT = () => {
    // Temporarily return false to hide budget items for everyone
    return false;
    
    // Original logic (uncomment when budget items are ready):
    // console.log('🔍 User data for IT check:', user);
    // console.log('🔍 User role:', user?.role);
    // console.log('🔍 User role_name:', user?.role_name);
    // console.log('🔍 User is_it:', user?.is_it);
    // console.log('🔍 hasRole IT:', hasRole('IT'));
    // console.log('🔍 hasRole 5:', hasRole('5'));
    // 
    // // Include Super Admin and IT roles
    // const isSuperAdmin = user?.role_name === 'Super Admin' || user?.role === 1;
    // const isITRole = hasRole('IT') || hasRole('5') || user?.is_it || user?.role === 5;
    // 
    // console.log('🔍 isSuperAdmin:', isSuperAdmin);
    // console.log('🔍 isITRole:', isITRole);
    // 
    // return isSuperAdmin || isITRole;
  };
  const [formData, setFormData] = useState({
    requisition_spec_id: '',
    requisition_title: '',
    requisition_number: '',
    requisition_user: '',
    requisition_quote: '',
    requisition_motivation: '',
    requisition_influence: '',
    requisition_declaration: '',
    files: []
  });

  useEffect(() => {
    if (specificationId) {
      fetchData();
    } else {
      toast.error('No specification ID provided');
      navigate('/specifications');
    }
  }, [specificationId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching specification with ID:', specificationId);
      
      // Validate specification ID
      if (!specificationId || isNaN(specificationId)) {
        throw new Error('Invalid specification ID');
      }
      
      // Fetch specification first
      let specData;
      try {
        specData = await getSpecification(specificationId);
        console.log('🔍 Specification data:', specData);
        
        // Check if specification data is valid
        if (!specData || !specData.specification) {
          throw new Error('Specification data not found or invalid');
        }
      } catch (specError) {
        console.error('� Error fetching specification:', specError);
        setSpecification(null);
        
        let errorMessage = 'Specification not found';
        if (specError.response?.status === 404) {
          errorMessage = `Specification with ID ${specificationId} not found`;
        } else if (specError.message) {
          errorMessage = specError.message;
        }
        
        toast.error(errorMessage);
        return; // Exit early if specification fetch fails
      }
      
      // Fetch budget items separately (non-critical, only for IT users)
      let budgetData = [];
      console.log('🔍 Is user IT?', isIT());
      if (isIT()) {
        try {
          budgetData = await getBudgetItems();
          console.log('🔍 Budget data:', budgetData);
        } catch (budgetError) {
          console.error('🚨 Error fetching budget items:', budgetError);
          // Don't show warning toast - budget items aren't ready yet
          console.log('🔍 Budget items endpoint not available yet (expected)');
        }
      } else {
        console.log('🔍 User is not IT, skipping budget items fetch');
      }
      
      // Set the successfully fetched data
      setSpecification(specData.specification);
      setQuotes(specData.submissions || []);
      setBudgetItems(budgetData?.data || budgetData || []);
      
      // Pre-populate form with specification data
      setFormData(prev => ({
        ...prev,
        requisition_spec_id: specData.specification.specification_id,
        requisition_title: specData.specification.specification_title,
        requisition_number: '', // Will be generated by backend
        requisition_user: specData.specification.specification_user,
        requisition_motivation: '',
        requisition_influence: '',
        requisition_declaration: '',
        files: []
      }));

      // Find the best evaluated quote (highest score) as default selection
      const evaluatedQuotes = specData.submissions?.filter(q => 
        q.quotation_evaluation && q.quotation_evaluation !== ''
      ) || [];
      
      if (evaluatedQuotes.length > 0) {
        const bestQuote = evaluatedQuotes.reduce((best, current) => {
          const bestScore = calculateEvaluationScore(best.quotation_evaluation);
          const currentScore = calculateEvaluationScore(current.quotation_evaluation);
          return currentScore > bestScore ? current : best;
        });
        setSelectedQuote(bestQuote);
      }
      
    } catch (error) {
      console.error('🚨 Unexpected error:', error);
      setSpecification(null);
      toast.error('An unexpected error occurred while loading the page');
    } finally {
      setLoading(false);
    }
  };

  const calculateEvaluationScore = (evaluation) => {
    try {
      let evalData;
      if (evaluation.startsWith('[') || evaluation.startsWith('{')) {
        evalData = JSON.parse(evaluation);
      } else {
        return 0;
      }
      
      if (Array.isArray(evalData)) {
        return evalData.reduce((sum, val) => sum + (parseInt(val) || 0), 0);
      } else if (typeof evalData === 'object') {
        return Object.values(evalData).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
      }
      return 0;
    } catch {
      return 0;
    }
  };

  const handleInputChange = (field, value) => {
    // Handle declaration logic like the RequisitionEdit implementation
    if (field === 'requisition_influence') {
      setFormData(prev => {
        const newFormData = { ...prev, [field]: value };
        
        // Auto-populate declaration based on influence selection
        if (value === '0') {
          // Set declaration to the "no interest" text
          newFormData.requisition_declaration = `I ${user?.user_full_name}, declare that I have no interest or been influenced to select the preferred vendor.`;
        } else if (value === '1') {
          // Set declaration to the "hereby declare" text (user can add more)
          newFormData.requisition_declaration = `I ${user?.user_full_name}, hereby declare that: `;
        }
        
        return newFormData;
      });
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleDateChange = (date) => {
    setFormData(prev => ({
      ...prev,
      expected_delivery: date
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.requisition_quote) {
      toast.error('Please select a quote to proceed with');
      return;
    }

    if (!formData.requisition_influence) {
      toast.error('Please complete the declaration section');
      return;
    }

    if (formData.requisition_influence === '1' && !formData.requisition_declaration.trim()) {
      toast.error('Please provide your declaration');
      return;
    }

    // if (quotes.length === 1 && !formData.requisition_motivation.trim()) {
    //   toast.error('Please provide motivation for fewer quotes');
    //   return;
    // }

    try {
      setSaving(true);
      
      const requisitionData = {
        requisition_spec_id: formData.requisition_spec_id,
        requisition_title: formData.requisition_title,
        requisition_user: formData.requisition_user,
        requisition_quote: formData.requisition_quote,
        requisition_motivation: formData.requisition_motivation,
        requisition_influence: formData.requisition_influence,
        requisition_declaration: formData.requisition_declaration,
        files: formData.files
      };

      console.log('Creating requisition with data:', requisitionData);
      
      const result = await createRequisition(requisitionData);
      console.log('Requisition creation result:', result);
      
      if (result.status === 1) {
        toast.success(result.message || 'Requisition created successfully');
        // Navigate back to specifications with state to trigger refresh
        navigate(`/dashboard/specifications/${specificationId}`, { 
          replace: true,
          state: { refreshData: Date.now() }
        });
      } else {
        throw new Error(result.message || 'Failed to create requisition');
      }
      
    } catch (error) {
      console.error('Error creating requisition:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Failed to create requisition';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Specific handling for permission errors
      if (error.response?.data?.message === 'Permission denied') {
        errorMessage = 'You do not have permission to create requisitions. Please contact your administrator.';
      }
      
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const parseFinancialValue = (value) => {
    if (!value && value !== 0) return 0;
    if (typeof value === 'number') return value;
    
    const cleanValue = value.toString()
      .replace(/\s/g, '')
      .replace(/[^\d.-]/g, '')
      .replace(/^-+/, '-')
      .replace(/\.+/g, '.');
    
    return parseFloat(cleanValue) || 0;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!specification) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <button
            onClick={() => navigate('/specifications')}
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Specifications
          </button>
        </div>
        
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="text-red-600 text-xl mb-2">Specification not found</div>
            <p className="text-gray-600">
              The specification with ID {specificationId} could not be found.
            </p>
            <button
              onClick={() => navigate('/specifications')}
              className="mt-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700"
            >
              Go to Specifications
            </button>
          </div>
        </div>
      </div>
    );
  }

  const evaluatedQuotes = quotes.filter(q => q.quotation_evaluation && q.quotation_evaluation !== '');

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/specifications/${specificationId}`)}
          className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Specification
        </button>
        
        <h1 className="text-3xl font-bold text-gray-900">Create Requisition</h1>
        <p className="text-gray-600 mt-1">
          Create a requisition based on the evaluated quotes for: {specification.specification_title}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Form */}
          <div className="flex-1 lg:w-2/3 space-y-6">
            {/* Requisition Details */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Requisition</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Requester
                  </label>
                  <Input
                    type="text"
                    value={specification?.user_full_name || ''}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expected Delivery Date
                  </label>
                  <Input
                    type="text"
                    value={specification?.specification_delivery || ''}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Project Name
                  </label>
                  <Input
                    type="text"
                    value={specification?.specification_title || ''}
                    readOnly
                    className="bg-gray-50"
                  />
                </div>
              </div>
            </div>

            {/* Quote Selection */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                Quotations
              </h2>
              
              {quotes.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-500 mb-2">No quotes available</div>
                  <p className="text-sm text-gray-400">
                    Please add quotes to the specification before creating a requisition.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {quotes.map((quote) => {
                    const isSelected = formData.requisition_quote === quote.quotation_id;
                    const score = calculateEvaluationScore(quote.quotation_evaluation);
                    
                    return (
                      <div
                        key={quote.quotation_id}
                        onClick={() => handleInputChange('requisition_quote', quote.quotation_id)}
                        className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 transform hover:scale-[1.02] hover:shadow-md ${
                          isSelected 
                            ? 'border-indigo-500 bg-indigo-50 shadow-lg ring-2 ring-indigo-200' 
                            : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                        }`}
                      >
                        {/* Selection indicator */}
                        <div className="absolute top-3 right-3">
                          {isSelected ? (
                            <div className="w-6 h-6 bg-indigo-600 rounded-full flex items-center justify-center shadow-sm">
                              <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              </svg>
                            </div>
                          ) : (
                            <div className="w-6 h-6 border-2 border-gray-300 rounded-full bg-white"></div>
                          )}
                        </div>

                        {/* Click indicator */}
                        <div className="absolute top-3 left-3">
                          <div className="flex items-center text-xs text-gray-500">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.121 2.122" />
                            </svg>
                            Click to select
                          </div>
                        </div>
                        
                        <div className="mt-6 mb-3">
                          <h3 className="font-semibold text-gray-900 text-lg pr-8">{quote.quotation_company_name}</h3>
                        </div>

                        <div className="flex items-center justify-between mb-3">
                          <div className="text-2xl font-bold text-gray-900">
                            R{parseFinancialValue(quote.quotation_total).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}
                          </div>
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full font-medium">
                            Score: {score}/16
                          </span>
                        </div>
                        
                        <div className="text-sm text-gray-600 space-y-1">
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            {quote.quotation_tel}
                          </div>
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            {(() => {
                              const emails = quote.quotation_email_address?.split(',') || [];
                              const firstEmail = emails.find(email => email.trim() !== '') || '';
                              return firstEmail.trim();
                            })()}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Motivation (if only 1 quote) */}
            {quotes.length === 1 && (
              <div className="bg-white shadow-lg rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Motivation</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Motivation for fewer quotes
                  </label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    rows="4"
                    value={formData.requisition_motivation}
                    onChange={(e) => handleInputChange('requisition_motivation', e.target.value)}
                    placeholder="Please provide motivation for having fewer than 3 quotes..."
                  />
                </div>
              </div>
            )}

            {/* Declaration */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Declaration</h2>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="requisition_influence"
                    value="0"
                    checked={formData.requisition_influence === '0'}
                    onChange={(e) => handleInputChange('requisition_influence', e.target.value)}
                    required
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-1"
                  />
                  <label className="text-sm text-gray-700">
                    I <strong>{user?.user_full_name}</strong>, declare that I have no interest or been influenced to select the preferred vendor.
                  </label>
                </div>

                <div className="flex items-start space-x-3">
                  <input
                    type="radio"
                    name="requisition_influence"
                    value="1"
                    checked={formData.requisition_influence === '1'}
                    onChange={(e) => handleInputChange('requisition_influence', e.target.value)}
                    required
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 mt-1"
                  />
                  <label className="text-sm text-gray-700">
                    I <strong>{user?.user_full_name}</strong>, hereby declare that:
                  </label>
                </div>

                {formData.requisition_influence === '1' && (
                  <div className="ml-7">
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                      rows="4"
                      value={formData.requisition_declaration}
                      onChange={(e) => handleInputChange('requisition_declaration', e.target.value)}
                      placeholder="Please provide your declaration..."
                      required
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Supporting Documents */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Supporting Documents</h2>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Supporting Files
                </label>
                <MultiFileInput
                  files={formData.files}
                  onChange={(files) => handleInputChange('files', files)}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.xlsx,.xls"
                  maxSize={10 * 1024 * 1024} // 10MB
                  multiple={true}
                />
                <p className="text-xs text-gray-500 mt-2">
                  Upload supporting documents such as specifications, drawings, or reference materials
                </p>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="flex-shrink-0 lg:w-1/3 space-y-6">
            {/* Specification Summary */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">Specification Summary</h3>
                <a 
                  href={`/dashboard/specifications/${specificationId}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-indigo-600 hover:text-indigo-800"
                >
                  View
                </a>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Title</div>
                  <div className="text-sm text-gray-900 mt-1">{specification.specification_title}</div>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Requestor</div>
                    <div className="text-sm text-gray-900 mt-1">{specification.user_full_name}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Division</div>
                    <div className="text-sm text-gray-900 mt-1">{specification.department_name}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Spec Date</div>
                    <div className="text-sm text-gray-900 mt-1">{formatDateForDisplay(specification.specification_date)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Delivery By</div>
                    <div className="text-sm text-gray-900 mt-1">{formatDateForDisplay(specification.specification_delivery)}</div>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Type</div>
                    <div className="text-sm mt-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        specification.specification_emergency === 1 
                          ? 'bg-orange-100 text-orange-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {specification.specification_emergency === 1 ? 'ESR' : 'RFQ'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">Quotes</div>
                    <div className="text-sm text-gray-900 mt-1">{quotes.length} Received</div>
                  </div>
                </div>
              </div>
            </div>
            {/* Actions */}
            <div className="bg-white shadow-lg rounded-lg p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>
              
              <div className="space-y-3">
                <button
                  type="submit"
                  disabled={saving || !formData.requisition_quote}
                  className={`w-full py-2 px-4 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed flex items-center justify-center ${
                    saving 
                      ? 'bg-indigo-400 text-white cursor-wait' 
                      : 'bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50'
                  }`}
                >
                  {saving && (
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  )}
                  {saving ? 'Creating...' : 'Create Requisition'}
                </button>
                
                <button
                  type="button"
                  onClick={() => navigate(`/specifications/${specificationId}`)}
                  className="w-full bg-gray-200 text-gray-800 py-2 px-4 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default CreateRequisition;
